import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "@/lib/toast";
import { DndContext, useDraggable, useDroppable, PointerSensor, useSensor, useSensors, DragOverlay } from "@dnd-kit/core";
import { Briefcase, ExternalLink, DollarSign, AlertCircle, GripVertical } from "lucide-react";

// Pipeline columns mirror backend PROJECT_STATUS_PROGRESS keys (server.py).
// Keep ids in lock-step or PATCH /api/projects/{id}/status will 400.
const COLUMNS = [
  { id: "ordered", title: "Ordered", color: "border-slate-400", dot: "bg-slate-400" },
  { id: "in_process", title: "In process", color: "border-blue-400", dot: "bg-blue-400" },
  { id: "awaiting_arrival", title: "Awaiting materials", color: "border-indigo-400", dot: "bg-indigo-400" },
  { id: "scheduling", title: "Scheduling", color: "border-amber-400", dot: "bg-amber-400" },
  { id: "scheduled", title: "Scheduled", color: "border-cyan-400", dot: "bg-cyan-400" },
  { id: "installed", title: "Installed", color: "border-purple-400", dot: "bg-purple-400" },
  { id: "completed", title: "Completed", color: "border-emerald-400", dot: "bg-emerald-400" },
];

export default function AdminPipeline() {
  const [projects, setProjects] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = () => api.get("/projects").then((r) => setProjects(r.data)).catch((err) => console.warn("Projects fetch:", err));
  useEffect(() => { load(); }, []);

  const onDragEnd = async (event) => {
    setActiveId(null);
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const project = projects.find((p) => p.project_id === active.id);
    if (!project || project.status === over.id) return;
    // Optimistic update
    const prev = project.status;
    setProjects((arr) => arr.map((p) => p.project_id === active.id ? { ...p, status: over.id } : p));
    try {
      await api.patch(`/projects/${active.id}/status`, { status: over.id });
      toast.success(`Moved to "${COLUMNS.find((c) => c.id === over.id)?.title}"`);
    } catch (err) {
      setProjects((arr) => arr.map((p) => p.project_id === active.id ? { ...p, status: prev } : p));
      toast.error(err?.response?.data?.detail || "Couldn't move");
    }
  };

  const colTotals = COLUMNS.map((c) => {
    const items = projects.filter((p) => p.status === c.id);
    const sum = items.reduce((s, p) => s + (p.amount || 0), 0);
    return { ...c, items, sum };
  });

  const dragged = projects.find((p) => p.project_id === activeId);

  return (
    <div data-testid="admin-pipeline">
      <div className="flex items-end justify-between mb-6 flex-wrap gap-3">
        <div>
          <p className="overline">Sales operations</p>
          <h1 className="text-3xl font-heading font-medium text-[#0A2540] dark:text-white mt-2">Pipeline</h1>
          <p className="text-sm text-[#475569] dark:text-slate-400 mt-1">Drag a project across columns to update its status.</p>
        </div>
        <Link to="/admin/projects" className="btn-outline-navy text-sm">Switch to list view</Link>
      </div>

      <DndContext sensors={sensors} onDragStart={(e) => setActiveId(e.active.id)} onDragEnd={onDragEnd}>
        <div className="flex gap-3 overflow-x-auto pb-4" style={{ scrollbarWidth: "thin" }}>
          {colTotals.map((c) => (
            <Column key={c.id} col={c} />
          ))}
        </div>
        <DragOverlay>
          {dragged ? <ProjectCardSkeleton project={dragged} /> : null}
        </DragOverlay>
      </DndContext>
    </div>
  );
}

function Column({ col }) {
  const { setNodeRef, isOver } = useDroppable({ id: col.id });
  return (
    <div ref={setNodeRef}
         className={`w-72 shrink-0 rounded-xl border-t-4 ${col.color} bg-slate-50 dark:bg-slate-900/40 ${isOver ? "ring-2 ring-[#C9A356] ring-offset-1" : ""}`}
         data-testid={`col-${col.id}`}>
      <div className="px-3 py-3 border-b border-[#E2E8F0] dark:border-slate-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className={`w-2 h-2 rounded-full ${col.dot}`}></span>
          <span className="font-medium text-[#0A2540] dark:text-white text-sm">{col.title}</span>
          <span className="text-[10px] text-[#94A3B8]">({col.items.length})</span>
        </div>
        <div className="text-xs text-[#475569] dark:text-slate-400 tabular-nums">${(col.sum || 0).toLocaleString()}</div>
      </div>
      <div className="p-2 space-y-2 min-h-[200px] max-h-[70vh] overflow-y-auto">
        {col.items.map((p) => <ProjectCard key={p.project_id} project={p} />)}
        {col.items.length === 0 && (
          <div className="text-center text-[10px] text-[#94A3B8] py-6">Drop here</div>
        )}
      </div>
    </div>
  );
}

function ProjectCard({ project }) {
  const { attributes, listeners, setNodeRef, transform, isDragging } = useDraggable({ id: project.project_id });
  const style = {
    transform: transform ? `translate3d(${transform.x}px, ${transform.y}px, 0)` : undefined,
    opacity: isDragging ? 0.3 : 1,
  };
  return (
    <div ref={setNodeRef} style={style} {...listeners} {...attributes}
         data-testid={`card-${project.project_id}`}
         className="bg-white dark:bg-slate-800 rounded-lg p-3 border border-[#E2E8F0] dark:border-slate-700 hover:border-[#C9A356] hover:shadow-md cursor-grab active:cursor-grabbing">
      <div className="flex items-start justify-between gap-2">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-medium text-[#0A2540] dark:text-white truncate">{project.title}</div>
          <div className="text-[10px] font-mono text-[#94A3B8] mt-0.5">{project.customer_code}</div>
        </div>
        <GripVertical className="w-3 h-3 text-[#94A3B8] shrink-0 mt-0.5" />
      </div>
      <div className="mt-2 flex items-center justify-between text-xs">
        <span className="text-[#475569] dark:text-slate-400 capitalize truncate">{project.project_type?.replace(/_/g, " ")}</span>
        {project.amount > 0 && <span className="font-bold tabular-nums text-[#C9A356]">${project.amount.toFixed(0)}</span>}
      </div>
      <Link to={`/admin/projects?focus=${project.project_id}`}
            onClick={(e) => e.stopPropagation()}
            className="mt-2 text-[10px] text-[#C9A356] hover:underline inline-flex items-center gap-1">
        Open <ExternalLink className="w-3 h-3" />
      </Link>
    </div>
  );
}

function ProjectCardSkeleton({ project }) {
  return (
    <div className="bg-white dark:bg-slate-800 rounded-lg p-3 border-2 border-[#C9A356] shadow-xl w-64 rotate-2">
      <div className="text-sm font-medium text-[#0A2540] dark:text-white truncate">{project.title}</div>
      <div className="text-[10px] font-mono text-[#94A3B8] mt-0.5">{project.customer_code}</div>
    </div>
  );
}
